import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { BehaviorSubject } from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ExtensionsService } from '@pages/checkout-app/services/extensions/extensions.service';
import {
  TIMER_STATES,
  EXTENSION_TIMER_CONFIGS,
  TIME_FORMATS,
  TIMER_DURATION_UNITS,
  LOW_TIMER_THRESHOLD_UNITS,
  APPEARANCE_TYPES,
  CHECKOUT_CONTENT_TIMER_PLACEMENTS,
} from '@pdqdev/pdq-shared/dist/utils/extensions-timer-utils';
import { BANNER_VARIANT_OPTIONS } from '@pages/checkout-app/checkout-layout/main/components/extensions/constants';
import { TimerPreviewMode, ExtensionComponentType } from '@pdqdev/pdq-shared/dist/interfaces';

interface DynamicFieldOption {
  name: string;
  searchTerm: string;
  description: string;
  displayName: string;
  category: string;
  replaceableName: string | null;
}

const COUNTDOWN_TOKEN: DynamicFieldOption = {
  name: '{countdown}',
  searchTerm: 'countdown',
  description: 'Display the countdown timer',
  displayName: 'Countdown Timer',
  category: 'Timer',
  replaceableName: null,
};

@UntilDestroy()
@Component({
  selector: 'timer-configuration',
  templateUrl: './timer-configuration.component.html',
  styleUrl: './timer-configuration.component.scss',
  standalone: false,
})
export class TimerConfigurationComponent implements OnInit, OnDestroy {
  @Input() timerControl: FormControl;
  @Input() extensionType: ExtensionComponentType | string;

  isPerRowPlacement = false;

  timerPreviewMode: TimerPreviewMode = TIMER_STATES.DEFAULT;
  timerDynamicFieldsOptions$ = new BehaviorSubject<DynamicFieldOption[]>([COUNTDOWN_TOKEN]);
  timerForm = new FormGroup({
    enableCountdownTimer: new FormControl<boolean>(false),
    timerDuration: new FormControl<number>(5),
    timerUnit: new FormControl<string>(TIMER_DURATION_UNITS.MINUTES),
    timeFormat: new FormControl<string>(TIME_FORMATS.DIGITAL),
    placement: new FormControl<string>(''),
    defaultMessage: new FormControl<string>('Only {countdown} left to claim this offer!'),
    lowTimerMessage: new FormControl<string>('Hurry - only {countdown} left!'),
    lowTimerThreshold: new FormControl<number>(60),
    lowTimerThresholdUnit: new FormControl<string>(LOW_TIMER_THRESHOLD_UNITS.SECONDS),
    lowTimerBackgroundColor: new FormControl<string>(APPEARANCE_TYPES.WARNING),
    backgroundColor: new FormControl<string>(APPEARANCE_TYPES.INFO),
    expiredMessage: new FormControl<string>('offer expired'),
    expiredBackgroundColor: new FormControl<string>(APPEARANCE_TYPES.CRITICAL),
    expirationBehavior: new FormControl<string>(''),
  });
  timerUnitOptions = [
    { label: 'Minutes', value: TIMER_DURATION_UNITS.MINUTES },
    { label: 'Hours', value: TIMER_DURATION_UNITS.HOURS },
  ];
  lowTimerThresholdUnitOptions = [
    { label: 'Seconds', value: LOW_TIMER_THRESHOLD_UNITS.SECONDS },
    { label: 'Minutes', value: LOW_TIMER_THRESHOLD_UNITS.MINUTES },
  ];

  constructor(private extensionsService: ExtensionsService) {}

  ngOnInit() {
    this.extensionsService.initDynamicFieldsAutocomplete();
    this.extensionsService.dynamicFieldsAutocomplete$
      .pipe(untilDestroyed(this))
      .subscribe((dynamicFields: DynamicFieldOption[]) => {
        if (dynamicFields) {
          this.timerDynamicFieldsOptions$.next([COUNTDOWN_TOKEN, ...dynamicFields]);
        }
      });

    const defaultPlacement = this.getDefaultPlacement();
    const defaultExpirationBehavior = this.getDefaultExpirationBehavior();

    const savedConfig = this.timerControl?.value;

    if (savedConfig) {
      this.timerForm.patchValue({
        enableCountdownTimer: savedConfig.enableCountdownTimer ?? false,
        timerDuration: savedConfig.timerDuration ?? 5,
        timerUnit: savedConfig.timerUnit ?? TIMER_DURATION_UNITS.MINUTES,
        timeFormat: savedConfig.timeFormat ?? TIME_FORMATS.DIGITAL,
        placement: savedConfig.placement ?? defaultPlacement,
        defaultMessage: savedConfig.defaultMessage ?? 'Only {countdown} left to claim this offer!',
        lowTimerMessage: savedConfig.lowTimerMessage ?? 'Hurry - only {countdown} left!',
        lowTimerThreshold: savedConfig.lowTimerThreshold ?? 60,
        lowTimerThresholdUnit: savedConfig.lowTimerThresholdUnit ?? LOW_TIMER_THRESHOLD_UNITS.SECONDS,
        lowTimerBackgroundColor: savedConfig.lowTimerBackgroundColor ?? APPEARANCE_TYPES.WARNING,
        backgroundColor: savedConfig.backgroundColor ?? APPEARANCE_TYPES.INFO,
        expiredMessage: savedConfig.expiredMessage ?? 'offer expired',
        expiredBackgroundColor: savedConfig.expiredBackgroundColor ?? APPEARANCE_TYPES.CRITICAL,
        expirationBehavior: savedConfig.expirationBehavior ?? defaultExpirationBehavior,
      });
    } else {
      this.timerForm.patchValue({
        placement: defaultPlacement,
        expirationBehavior: defaultExpirationBehavior,
      });
    }

    this.updateIsPerRowPlacement();
    this.timerForm.valueChanges.pipe(untilDestroyed(this)).subscribe(() => {
      this.updateIsPerRowPlacement();
      this.timerControl?.setValue(this.getTimerConfiguration());
      this.timerControl?.markAsDirty();
    });
  }

  ngOnDestroy() {}

  private getExtensionConfig() {
    switch (this.extensionType) {
      case ExtensionComponentType.CUSTOM_UPSELL:
        return EXTENSION_TIMER_CONFIGS.DIGITAL_UPSELL;
      case ExtensionComponentType.CHECKOUT_UPSELL:
        return EXTENSION_TIMER_CONFIGS.UPSELL;
      case ExtensionComponentType.CHECKOUT_CONTENT:
        return EXTENSION_TIMER_CONFIGS.CHECKOUT_CONTENT;
      default:
        return EXTENSION_TIMER_CONFIGS.CHECKOUT_CONTENT;
    }
  }

  private getDefaultPlacement(): string {
    const config = this.getExtensionConfig();
    const placements = Object.values(config.TIMER_PLACEMENTS) as string[];
    return placements[0] || '';
  }

  private getDefaultExpirationBehavior(): string {
    const config = this.getExtensionConfig();
    const behaviors = Object.values(config.EXPIRATION_BEHAVIORS) as string[];
    return behaviors[0] || '';
  }

  get timerPlacements() {
    if (this.extensionType === ExtensionComponentType.CHECKOUT_CONTENT) {
      return CHECKOUT_CONTENT_TIMER_PLACEMENTS;
    }
    return this.getExtensionConfig().TIMER_PLACEMENTS;
  }

  get expirationBehaviors() {
    return this.getExtensionConfig().EXPIRATION_BEHAVIORS;
  }

  onTimerUnitChange(event: any) {
    this.timerForm.get('timerUnit')?.setValue(event.value);
    this.timerForm.get('timerUnit')?.markAsDirty();
  }

  onLowTimerThresholdUnitChange(event: any) {
    this.timerForm.get('lowTimerThresholdUnit')?.setValue(event.value);
    this.timerForm.get('lowTimerThresholdUnit')?.markAsDirty();
  }

  getTimerConfiguration() {
    return {
      ...this.timerForm.value,
      timerPreviewMode: this.timerPreviewMode,
    };
  }

  setTimerPreviewMode(mode: TimerPreviewMode) {
    this.timerPreviewMode = mode;
    this.timerControl?.setValue(this.getTimerConfiguration());
    this.timerControl?.markAsDirty();
  }

  readonly bannerVariantOptions = BANNER_VARIANT_OPTIONS;
  readonly TIMER_STATES = TIMER_STATES;
  readonly TIME_FORMATS = TIME_FORMATS;

  private updateIsPerRowPlacement() {
    this.isPerRowPlacement =
      this.extensionType === ExtensionComponentType.CHECKOUT_CONTENT &&
      this.timerForm.get('placement')?.value === CHECKOUT_CONTENT_TIMER_PLACEMENTS.PER_ROW;
  }
}
